import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Platform,
  ActivityIndicator,
  FlatList,
  TextInput,
  RefreshControl,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { ScreenLayout, Header, ConfirmationModal } from '../components';
import { Button } from '../components/design-system';
import { apiService, DataList, DataListRecord, DataListColumn } from '../services/api';
import { useAppContext, Screen } from '../contexts/AppContext';
import { createTabPressHandler } from '../navigation/handleTabPress';
import { useLocale } from '../contexts/LocaleContext';
import { useRTLStyles } from '../hooks/useRTLStyles';
import { colors, spacing, typography } from '../theme';
import { designTokens } from '../theme/design-system';
import { useThemedStyles } from '../hooks/useThemedStyles';
import { FilterField, FilterValues, SortOption, useListFilters } from '../components/listFilters/listFilters';
import { FilterSheet } from '../components/listFilters/FilterSheet';
import { ListFilterBar } from '../components/listFilters/ListFilterBar';

const PRESENCE_TYPES = ['image', 'video', 'voice', 'object', 'relation', 'repeater'];

/** One filter per column, matched to its type. */
function columnFields(columns: DataListColumn[], t: (k: any, p?: any) => string): FilterField[] {
  return [...columns].sort((a, b) => a.order - b.order).map((c): FilterField => {
    const enumValues = (c as any).constraints?.enum as string[] | undefined;
    if (enumValues?.length) return { key: c.id, label: c.name, type: 'multi', options: enumValues.map((v) => ({ value: v, label: v })) };
    switch (c.type) {
      case 'number': return { key: c.id, label: c.name, type: 'range' };
      case 'date': return { key: c.id, label: c.name, type: 'date' };
      case 'boolean': return { key: c.id, label: c.name, type: 'choice', options: [{ value: 'true', label: t('common.yes') }, { value: 'false', label: t('common.no') }] };
      default:
        if (PRESENCE_TYPES.includes(c.type)) return { key: c.id, label: c.name, type: 'choice', options: [{ value: 'hasValue', label: t('listFilters.hasValue') }, { value: 'noValue', label: t('listFilters.noValue') }] };
        return { key: c.id, label: c.name, type: 'text', placeholder: t('dataLists.records.filterContains') };
    }
  });
}

/** Filter values → the API's column filters. */
function columnFilters(fields: FilterField[], values: FilterValues) {
  return fields.flatMap((f) => {
    const v = values[f.key];
    if (v === undefined || v === '' || (Array.isArray(v) && !v.length)) return [];
    if (f.type === 'multi') return [{ columnId: f.key, op: 'in', values: v }];
    if (f.type === 'range') return [{ columnId: f.key, op: 'between', min: v.min, max: v.max }];
    if (f.type === 'date') return [{ columnId: f.key, op: 'between', min: v.from, max: v.to }];
    if (f.type === 'choice') return [{ columnId: f.key, op: v }];
    return [{ columnId: f.key, op: 'contains', value: String(v) }];
  });
}

interface DataListRecordsScreenProps {
  listId?: string;
}

const PAGE_SIZE = 20;

// System Datalists wrap a real module — adding a record should use that
// module's own purpose-built screen (reached from e.g. /contacts), not the
// generic column-driven form. `addParam` mirrors the fixed 'default' fallback
// ProductScannerScreen already uses when no catalogue is known.
const SYSTEM_MODULE_ROUTES: Record<
  string,
  { addScreen: Screen; addParam?: string; bulkScreen?: Screen }
> = {
  contacts: { addScreen: 'contact-create', bulkScreen: 'bulk-contact-upload' },
  products: { addScreen: 'product-create', addParam: 'default', bulkScreen: 'product-bulk-import' },
  catalog: { addScreen: 'catalogue-create' },
  calendar: { addScreen: 'event-create' },
  orders: { addScreen: 'order-add' },
  vendors: { addScreen: 'vendor-create', bulkScreen: 'vendor-bulk-upload' },
  quotations: { addScreen: 'quotation-create' },
};

// A System Datalist wraps a real module — the LISTING view should be that
// module's own list screen too, restricted to just this list's member
// records ("scoped mode"), not the generic column-driven table this screen
// otherwise renders for custom lists. On mount, a system list immediately
// redirects here instead of rendering its own table.
// 'calendar' is deliberately OMITTED — the only calendar-events screen in the
// app (EventListScreen) always shows ONE specific calendar's events, not a
// business-wide list; there's no natural target to redirect a calendar
// System Datalist into yet, so it still falls back to the generic table
// below rather than being force-fit into a single-calendar view.
const SYSTEM_MODULE_LIST_SCREENS: Record<string, Screen> = {
  contacts: 'contacts',
  products: 'products-list',
  catalog: 'catalogues',
  orders: 'orders',
  vendors: 'vendors',
  quotations: 'quotations',
};

export const DataListRecordsScreen: React.FC<DataListRecordsScreenProps> = ({ listId }) => {
  const styles = useThemedStyles(getStyles);
  const { navigateTo, activeTab, setActiveTab, showNotification } = useAppContext();
  const handleTabPress = createTabPressHandler(navigateTo, setActiveTab);
  const { t } = useLocale();
  const rtl = useRTLStyles();


  const [list, setList] = useState<DataList | null>(null);
  const [records, setRecords] = useState<DataListRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const listFilters = useListFilters(`data-list-records:${listId}`);
  const search = listFilters.state.search;
  const [showFilters, setShowFilters] = useState(false);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [page, setPage] = useState(0);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [recordToDelete, setRecordToDelete] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  const isReadOnly = list?.dataSource === 'webhook';
  const isSystemList = list?.columnsSource === 'system';
  const systemRoute = list?.systemModule ? SYSTEM_MODULE_ROUTES[list.systemModule] : undefined;

  const handleAddPress = () => {
    if (isSystemList && systemRoute) {
      navigateTo(systemRoute.addScreen, systemRoute.addParam, { listId });
    } else {
      navigateTo('data-list-record-create', undefined, { listId });
    }
  };

  const handleBulkAddPress = () => {
    if (systemRoute?.bulkScreen) {
      navigateTo(systemRoute.bulkScreen, systemRoute.addParam, { listId });
    }
  };

  const displayColumns: DataListColumn[] = React.useMemo(() => {
    if (!list) return [];
    return [...list.columns].sort((a, b) => a.order - b.order).slice(0, 3);
  }, [list]);

  const recordFields = useMemo(() => (list && list.columnsSource !== 'system' ? columnFields(list.columns || [], t) : []), [list, t]);
  const recordSorts = useMemo<SortOption[]>(() => [
    { key: 'newest', label: t('dataLists.sort.newest'), sortBy: 'createdAt', sortOrder: 'desc', icon: 'arrow-down-outline' },
    { key: 'oldest', label: t('dataLists.sort.oldest'), sortBy: 'createdAt', sortOrder: 'asc', icon: 'arrow-up-outline' },
    { key: 'updated', label: t('dataLists.sort.updated'), sortBy: 'updatedAt', sortOrder: 'desc', icon: 'time-outline' },
    // Sort by the list's own text / number / date columns.
    ...[...(list?.columns || [])].sort((a, b) => a.order - b.order).filter((c) => ['text', 'number', 'date', 'phone'].includes(c.type)).slice(0, 6).flatMap((c): SortOption[] => (
      c.type === 'text' || c.type === 'phone'
        ? [{ key: `${c.id}:asc`, label: t('dataLists.sort.columnAsc', { name: c.name }), sortBy: `values.${c.id}`, sortOrder: 'asc', icon: 'text-outline' }]
        : [{ key: `${c.id}:desc`, label: t('dataLists.sort.columnDesc', { name: c.name }), sortBy: `values.${c.id}`, sortOrder: 'desc', icon: 'trending-down-outline' },
           { key: `${c.id}:asc`, label: t('dataLists.sort.columnAsc', { name: c.name }), sortBy: `values.${c.id}`, sortOrder: 'asc', icon: 'trending-up-outline' }]
    )),
  ], [list, t]);

  const loadList = useCallback(async () => {
    if (!listId) return;
    try {
      const res = await apiService.getDataList(listId);
      if (res.statusCode === 1000 && res.data) setList(res.data);
    } catch (err) {
      console.error('[DataListRecordsScreen] Failed to load list:', err);
    }
  }, [listId]);

  const loadRecords = useCallback(
    async (pageToLoad = 0, reset = false) => {
      if (!listId) return;
      try {
        setError(null);
        if (reset) setLoading(true);
        const sort = recordSorts.find((x) => x.key === listFilters.state.sort) || recordSorts[0];
        const res = await apiService.getDataListRecords(listId, {
          search: search.trim() || undefined,
          limit: PAGE_SIZE,
          offset: pageToLoad * PAGE_SIZE,
          sortBy: sort.sortBy,
          sortOrder: sort.sortOrder,
          filters: columnFilters(recordFields, listFilters.state.filters),
        });
        if (res.statusCode === 1000 && Array.isArray(res.data)) {
          const incoming = res.data;
          setRecords((prev) => (reset ? incoming : [...prev, ...incoming]));
          setTotal(res.additionalInfo?.total ?? incoming.length);
          setHasMore(incoming.length === PAGE_SIZE);
          setPage(pageToLoad);
        } else {
          setError(res.errors?.[0]?.message || t('dataLists.records.loadFailed'));
          if (reset) setRecords([]);
        }
      } catch (err: any) {
        setError(err?.message || t('dataLists.records.loadFailed'));
        if (reset) setRecords([]);
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [listId, search, t, listFilters.state, recordFields, recordSorts]
  );

  useEffect(() => {
    loadList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listId]);

  // System lists redirect into the real module's own list screen, scoped to
  // just this list's member records, rather than rendering the generic table
  // below — that table stays exactly as-is for custom lists only. Only true
  // when a scoped-mode target screen actually exists for this module (see
  // SYSTEM_MODULE_LIST_SCREENS's comment on 'calendar' being omitted) — a
  // system list whose module has no target must keep rendering the generic
  // table, not get stuck on a redirect spinner that never navigates anywhere.
  const redirectingToScopedList = !!(
    list && list.columnsSource === 'system' && list.systemModule && listId &&
    SYSTEM_MODULE_LIST_SCREENS[list.systemModule]
  );
  useEffect(() => {
    if (redirectingToScopedList && list?.systemModule && listId) {
      const targetScreen = SYSTEM_MODULE_LIST_SCREENS[list.systemModule];
      if (targetScreen) {
        navigateTo(targetScreen, undefined, { scopedToDataListId: listId, scopedListName: list.name });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [redirectingToScopedList, list?.systemModule, list?.name, listId]);

  // Reload when filters change (search debounced), once the list (its columns) and remembered filters are read.
  const filterKey = JSON.stringify([listFilters.state.filters, listFilters.state.sort, search]);
  useEffect(() => {
    if (redirectingToScopedList || !listFilters.ready || !list) return;
    const timer = setTimeout(() => loadRecords(0, true), search ? 400 : 0);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listId, filterKey, redirectingToScopedList, listFilters.ready, !!list]);

  const handleRefresh = () => {
    setRefreshing(true);
    loadRecords(0, true);
  };

  const handleLoadMore = () => {
    if (!loading && hasMore && !error) loadRecords(page + 1, false);
  };

  const confirmDelete = async () => {
    if (!recordToDelete || !listId) return;
    try {
      setDeleting(true);
      const res = await apiService.deleteDataListRecord(listId, recordToDelete);
      if (res.statusCode === 1000) {
        showNotification('success', t('dataLists.notifications.successTitle'), t('dataLists.records.delete.success'));
        setShowDeleteModal(false);
        setRecordToDelete(null);
        loadRecords(0, true);
      } else {
        showNotification('error', t('dataLists.notifications.errorTitle'), res.errors?.[0]?.message || t('dataLists.records.delete.failed'));
        setShowDeleteModal(false);
        setRecordToDelete(null);
      }
    } catch (err: any) {
      showNotification('error', t('dataLists.notifications.errorTitle'), err?.message || t('dataLists.records.delete.failed'));
      setShowDeleteModal(false);
      setRecordToDelete(null);
    } finally {
      setDeleting(false);
    }
  };

  const formatValue = (col: DataListColumn, raw: any): string => {
    if (raw == null || raw === '') return t('dataLists.records.emptyValue');
    if (col.type === 'boolean') return raw ? t('common.yes') : t('common.no');
    if (col.type === 'object') return typeof raw === 'string' ? raw : JSON.stringify(raw);
    if (col.type === 'date') {
      const d = new Date(raw);
      return isNaN(d.getTime()) ? String(raw) : d.toLocaleDateString();
    }
    return String(raw);
  };

  const renderRecord = ({ item }: { item: DataListRecord }) => (
    <TouchableOpacity
      style={styles.card}
      activeOpacity={0.7}
      onPress={() => navigateTo('data-list-record-edit', item._id, { listId })}
    >
      <View style={styles.cardBody}>
        {displayColumns.map((col) => (
          <View key={col.id} style={[styles.valueRow, { flexDirection: rtl.rowDirection }]}>
            <Text style={[styles.valueLabel, { textAlign: rtl.textStart }]} numberOfLines={1}>{col.name}</Text>
            <Text style={[styles.valueText, { textAlign: rtl.textStart }]} numberOfLines={1}>{formatValue(col, item.values?.[col.id])}</Text>
          </View>
        ))}
      </View>
      {!isReadOnly && (
        <TouchableOpacity
          style={styles.deleteButton}
          onPress={() => {
            setRecordToDelete(item._id);
            setShowDeleteModal(true);
          }}
          hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
        >
          <Ionicons name="trash-outline" size={18} color={colors.error} />
        </TouchableOpacity>
      )}
    </TouchableOpacity>
  );

  const renderContent = () => {
    if (redirectingToScopedList) {
      return (
        <View style={styles.centerContainer}>
          <ActivityIndicator size="large" color={colors.primary} />
        </View>
      );
    }
    if (loading && records.length === 0) {
      return (
        <View style={styles.centerContainer}>
          <ActivityIndicator size="large" color={colors.primary} />
          <Text style={styles.centerText}>{t('dataLists.records.loading')}</Text>
        </View>
      );
    }
    if (error && records.length === 0) {
      return (
        <View style={styles.centerContainer}>
          <Ionicons name="alert-circle-outline" size={64} color={colors.error} />
          <Text style={styles.errorText}>{error}</Text>
          <Button title={t('common.retry')} onPress={() => loadRecords(0, true)} variant="primary" style={styles.retryButton} />
        </View>
      );
    }
    if (records.length === 0) {
      return (
        <View style={styles.centerContainer}>
          <Ionicons name="documents-outline" size={64} color={colors.textLight} />
          <Text style={styles.emptyTitle}>{t('dataLists.records.empty.title')}</Text>
          <Text style={styles.errorText}>{isReadOnly ? t('dataLists.records.empty.webhookMessage') : t('dataLists.records.empty.message')}</Text>
          {!isReadOnly && (
            <Button title={t('dataLists.records.add')} onPress={handleAddPress} variant="primary" style={styles.retryButton} />
          )}
        </View>
      );
    }
    return (
      <FlatList
        data={records}
        renderItem={renderRecord}
        keyExtractor={(item) => item._id}
        contentContainerStyle={styles.listContent}
        showsVerticalScrollIndicator={false}
        onEndReached={handleLoadMore}
        onEndReachedThreshold={0.4}
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={handleRefresh} colors={[colors.primary as string]} />}
      />
    );
  };

  return (
    <ScreenLayout
      header={
        <Header
          title={list?.name || t('dataLists.records.title')}
          showBackButton
          onBackPress={() => navigateTo('data-lists')}
          showNotifications
          showMessages
          onNotificationPress={() => navigateTo('notifications')}
          onMessagePress={() => navigateTo('chat')}
          showNavigation={Platform.OS === 'web'}
          activeTab={activeTab}
          onTabPress={handleTabPress}
        />
      }
      showBottomTabs={Platform.OS !== 'web'}
      onLeftPaneClose={() => {}}
      leftPaneContent="settings"
      onSettingsItemPress={async (itemId) => {
        const { setPendingSettingsItem } = await import('../utils/tempStorage');
        await setPendingSettingsItem(itemId);
        navigateTo('settings');
      }}
    >
      <View style={styles.container}>
        <View style={styles.header}>
          <View style={[styles.searchContainer, { flexDirection: rtl.rowDirection }]}>
            <Text style={[styles.searchInput, styles.headerSubtitle, { textAlign: rtl.textStart }]} numberOfLines={1}>{list?.name || ''}</Text>
            {!loading && (
              <Text style={styles.headerSubtitle}>
                {total === 1 ? t('dataLists.records.subtitle.countOne') : t('dataLists.records.subtitle.count', { count: total })}
              </Text>
            )}
            {!isReadOnly && (
              <TouchableOpacity style={styles.addButton} onPress={handleAddPress} activeOpacity={0.8} accessibilityLabel={t('common.add')}>
                <Ionicons name="add" size={20} color={colors.white} />
              </TouchableOpacity>
            )}
          </View>

          {isReadOnly && (
            <View style={[styles.badgeRow, { flexDirection: rtl.rowDirection }]}>
              <Ionicons name="flash-outline" size={14} color={colors.primary} />
              <Text style={styles.badgeText}>{t('dataLists.records.liveWebhookBadge')}</Text>
            </View>
          )}

          {!isReadOnly && isSystemList && systemRoute?.bulkScreen && (
            <View style={[styles.templateRow, { flexDirection: rtl.rowDirection }]}>
              <TouchableOpacity style={[styles.templateButton, { flexDirection: rtl.rowDirection }]} onPress={handleBulkAddPress}>
                <Ionicons name="cloud-upload-outline" size={16} color={colors.primary} />
                <Text style={styles.templateButtonText}>{t('dataLists.records.bulkAdd')}</Text>
              </TouchableOpacity>
            </View>
          )}
        </View>

        <ListFilterBar
          controller={listFilters}
          fields={recordFields}
          sorts={recordSorts}
          onOpenFilters={() => setShowFilters(true)}
          searchPlaceholder={t('dataLists.records.searchPlaceholder')}
        />

        {renderContent()}

        <FilterSheet
          visible={showFilters}
          title={t('dataLists.records.filterTitle')}
          fields={recordFields}
          sorts={recordSorts}
          value={listFilters.state}
          onApply={listFilters.apply}
          onClose={() => setShowFilters(false)}
        />

        <ConfirmationModal
          visible={showDeleteModal}
          onClose={() => {
            setShowDeleteModal(false);
            setRecordToDelete(null);
          }}
          onConfirm={confirmDelete}
          title={t('dataLists.records.delete.title')}
          message={t('dataLists.records.delete.message')}
          confirmText={t('dataLists.delete.confirm')}
          cancelText={t('dataLists.delete.cancel')}
          type="error"
          loading={deleting}
        />
      </View>
    </ScreenLayout>
  );
};

const getStyles = () => StyleSheet.create({
  container: { flex: 1, backgroundColor: colors.lightGray },
  header: {
    backgroundColor: colors.background.surface,
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.lg,
    ...designTokens.shadows.sm,
  },
  headerSubtitle: {
    fontSize: typography.fontSize.xs,
    color: colors.textLight,
    fontFamily: typography.fontFamily.regular,
    marginHorizontal: spacing.sm,
  },
  addButton: {
    width: 36,
    height: 36,
    borderRadius: 18,
    backgroundColor: colors.primary,
    justifyContent: 'center',
    alignItems: 'center',
  },
  searchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.sm,
    backgroundColor: colors.background.primary,
    borderRadius: designTokens.borderRadius.md,
  },
  searchInput: { flex: 1, fontSize: typography.fontSize.base, color: colors.text.primary, fontFamily: typography.fontFamily.regular, paddingVertical: 0 },
  badgeRow: { flexDirection: 'row', alignItems: 'center', gap: 4, marginTop: spacing.xs, paddingHorizontal: spacing.xs },
  badgeText: { fontSize: typography.fontSize.xs, color: colors.primary, fontFamily: typography.fontFamily.medium },
  templateRow: { flexDirection: 'row', gap: spacing.sm, marginTop: spacing.sm },
  templateButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs,
    borderRadius: designTokens.borderRadius.sm,
    backgroundColor: String(colors.primary) + '10',
  },
  templateButtonText: { fontSize: typography.fontSize.xs, color: colors.primary, fontFamily: typography.fontFamily.semibold },
  listContent: { padding: spacing.md, paddingBottom: Platform.OS !== 'web' ? 100 : spacing.lg },
  card: {
    backgroundColor: colors.background.surface,
    borderRadius: designTokens.borderRadius.md,
    padding: spacing.md,
    marginBottom: spacing.sm,
    flexDirection: 'row',
    alignItems: 'center',
    ...designTokens.shadows.sm,
  },
  cardBody: { flex: 1 },
  valueRow: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm, marginBottom: spacing.xs / 2 },
  valueLabel: { width: 100, fontSize: typography.fontSize.xs, color: colors.textLight, fontFamily: typography.fontFamily.medium },
  valueText: { flex: 1, fontSize: typography.fontSize.sm, color: colors.text.primary, fontFamily: typography.fontFamily.regular },
  deleteButton: { padding: spacing.sm },
  centerContainer: { flex: 1, justifyContent: 'center', alignItems: 'center', padding: spacing.xxl },
  centerText: { marginTop: spacing.md, fontSize: typography.fontSize.base, color: colors.textLight, fontFamily: typography.fontFamily.regular },
  emptyTitle: {
    fontSize: typography.fontSize.xl,
    fontWeight: '600',
    color: colors.textDark,
    marginTop: spacing.lg,
    marginBottom: spacing.sm,
    fontFamily: typography.fontFamily.semibold,
    textAlign: 'center',
  },
  errorText: { fontSize: typography.fontSize.base, color: colors.textLight, textAlign: 'center', marginBottom: spacing.xl, fontFamily: typography.fontFamily.regular },
  retryButton: { minWidth: 160 },
});
